import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  getStreamableMessageContent,
  type StreamableChatMessage,
} from './streaming-chat-message';

interface UseStreamingChatScrollOptions {
  containerRef: RefObject<HTMLElement>;
  messages: ReadonlyArray<StreamableChatMessage>;
  enabled?: boolean;
  isLoading?: boolean;
}

const isAssistantMessage = (message: StreamableChatMessage): boolean =>
  message.sender !== 'user';

const noop = () => undefined;

const scheduleLayoutScroll = (scroll: () => void): (() => void) => {
  scroll();

  if (typeof window === 'undefined') return noop;

  const cleanupTasks: Array<() => void> = [];

  const firstFrameId = window.requestAnimationFrame(() => {
    scroll();

    const secondFrameId = window.requestAnimationFrame(scroll);
    cleanupTasks.push(() => window.cancelAnimationFrame(secondFrameId));
  });
  cleanupTasks.push(() => window.cancelAnimationFrame(firstFrameId));

  const timeoutIds = [80, 180, 360].map(delay =>
    window.setTimeout(scroll, delay)
  );
  cleanupTasks.push(
    ...timeoutIds.map(timeoutId => () => window.clearTimeout(timeoutId))
  );

  return () => {
    cleanupTasks.forEach(cleanup => cleanup());
  };
};

const findMessageElement = (
  container: HTMLElement,
  messageId: string
): HTMLElement | null => {
  const selectors = [
    '[data-chat-final-content-id]',
    '[data-chat-message-content-id]',
    '[data-chat-message-id]',
  ];

  for (const selector of selectors) {
    const nodes = container.querySelectorAll<HTMLElement>(selector);

    for (const node of nodes) {
      if (
        node.getAttribute('data-chat-final-content-id') === messageId ||
        node.getAttribute('data-chat-message-content-id') === messageId ||
        node.getAttribute('data-chat-message-id') === messageId
      ) {
        return node;
      }
    }
  }

  return null;
};

const getStreamingActivitySignature = (
  messages: ReadonlyArray<StreamableChatMessage>
): string => {
  const streamingMessage = [...messages]
    .reverse()
    .find(message => isAssistantMessage(message) && message.isStreaming);

  if (!streamingMessage) return '';

  const progressSignature = (streamingMessage.progressEvents ?? [])
    .map(
      progressEvent =>
        `${progressEvent.id}:${progressEvent.text}:${progressEvent.count ?? 1}`
    )
    .join('|');
  const toolSignature = (streamingMessage.toolEvents ?? [])
    .map(
      toolEvent =>
        `${toolEvent.id}:${toolEvent.status ?? ''}:${toolEvent.success ?? ''}`
    )
    .join('|');

  return [
    streamingMessage.id,
    streamingMessage.progressText ?? '',
    progressSignature,
    toolSignature,
    streamingMessage.content.length,
  ].join('::');
};

export const useStreamingChatScroll = ({
  containerRef,
  messages,
  enabled = true,
  isLoading = false,
}: UseStreamingChatScrollOptions) => {
  const streamedMessageIdsRef = useRef<Set<string>>(new Set());

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const container = containerRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior });
    },
    [containerRef]
  );

  const scrollMessageIntoView = useCallback(
    (messageId: string, behavior: ScrollBehavior = 'auto'): (() => void) => {
      const container = containerRef.current;
      if (!container) return noop;

      const scrollToMessage = () => {
        const messageElement = findMessageElement(container, messageId);
        if (!messageElement) return;

        const containerRect = container.getBoundingClientRect();
        const messageRect = messageElement.getBoundingClientRect();
        const top = Math.max(
          container.scrollTop + messageRect.top - containerRect.top - 8,
          0
        );

        container.scrollTo({ top, behavior });
      };

      return scheduleLayoutScroll(scrollToMessage);
    },
    [containerRef]
  );

  const streamingActivitySignature = useMemo(
    () => getStreamingActivitySignature(messages),
    [messages]
  );

  useLayoutEffect(() => {
    if (!enabled) return;
    scrollToBottom('auto');
  }, [enabled, isLoading, messages.length, scrollToBottom]);

  useLayoutEffect(() => {
    if (!enabled || !streamingActivitySignature) return;
    scrollToBottom('auto');
  }, [enabled, scrollToBottom, streamingActivitySignature]);

  useLayoutEffect(() => {
    if (!enabled) return;

    const streamedMessageIds = streamedMessageIdsRef.current;

    messages.forEach(message => {
      if (isAssistantMessage(message) && message.isStreaming) {
        streamedMessageIds.add(message.id);
      }
    });

    const completedStreamingMessage = [...messages]
      .reverse()
      .find(
        message =>
          isAssistantMessage(message) &&
          streamedMessageIds.has(message.id) &&
          !message.isStreaming &&
          message.status === 'completed' &&
          Boolean(getStreamableMessageContent(message).trim())
      );

    if (completedStreamingMessage) {
      streamedMessageIds.delete(completedStreamingMessage.id);
      return scrollMessageIntoView(completedStreamingMessage.id, 'auto');
    }
  }, [enabled, messages, scrollMessageIntoView]);

  return { scrollToBottom };
};
